import { useEffect, useRef } from 'react'
import { CircleAlert, CircleCheck } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ToastState = { tone: 'success' | 'error'; message: string }

const AUTO_HIDE_MS = 4000

type ToastProps = {
  toast: ToastState
  onDismiss: () => void
  /** C'è una barra d'azione fissa in basso: il toast sale sopra di lei. */
  aboveBar?: boolean
}

/** Esito di un'azione dell'arbitro, in basso sotto il pollice: sparisce da solo dopo 4s, o subito col nuovo esito.
 * `pointer-events-none`: solo informativo, i tocchi passano a quello che copre. */
export function Toast({ toast, onDismiss, aboveBar = false }: ToastProps) {
  // `onDismiss` è una closure nuova a ogni render del genitore (che col cronometro ri-renderizza ogni
  // secondo): tenuta in un ref, così il timer riparte solo con un nuovo esito e non a ogni render.
  const dismissRef = useRef(onDismiss)
  dismissRef.current = onDismiss
  useEffect(() => {
    const timer = setTimeout(() => dismissRef.current(), AUTO_HIDE_MS)
    return () => clearTimeout(timer)
  }, [toast])

  const Icon = toast.tone === 'error' ? CircleAlert : CircleCheck
  return (
    <div
      role="status"
      className={cn(
        'pointer-events-none fixed inset-x-4 z-50 mx-auto flex max-w-sm items-center gap-2.5 rounded-2xl border border-paper/15 bg-ink px-4 py-3 font-body text-sm text-paper shadow-[0_12px_32px_-12px_black] motion-safe:animate-sheet-up',
        aboveBar ? 'bottom-[calc(5.5rem+env(safe-area-inset-bottom))]' : 'bottom-[calc(1rem+env(safe-area-inset-bottom))]',
      )}
    >
      <Icon aria-hidden="true" className={cn('size-5 shrink-0', toast.tone === 'error' ? 'text-coral' : 'text-sea')} />
      {toast.message}
    </div>
  )
}
